import { createFileRoute, useParams } from '@tanstack/react-router';

import { usePurchaseCycleSummary } from '@/features/purchase-cycle';
import { useSyncStatus } from '@/features/sync';
import { AnalysisGuide } from '@/shared/ui/analysis-guide';
import { AnimatedContainer } from '@/shared/ui/animated';
import { MemberOnlyNotice } from '@/shared/ui/member-only-notice';
import { NeedsSyncState } from '@/shared/ui/needs-sync-state';
import { PageHeader } from '@/shared/ui/page-header';
import { testIds } from '@/shared/testing';
import { AppLayout } from '../../-components/app-layout';
import { CycleDistributionChart } from './-components/cycle-distribution-chart';
import { OverdueCustomersTable } from './-components/overdue-customers-table';
import { ProductCyclesTable } from './-components/product-cycles-table';
import { SummaryCards } from './-components/summary-cards';
import { UpcomingCustomersTable } from './-components/upcoming-customers-table';

export const Route = createFileRoute('/_authenticated/$mallId/analysis/purchase-cycle/')({
  component: PurchaseCyclePage,
});

function PurchaseCyclePage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/analysis/purchase-cycle/' });
  const { data: syncStatus, isLoading: isSyncLoading } = useSyncStatus(mallId);
  const { data: summary, isLoading: isSummaryLoading } = usePurchaseCycleSummary();

  const needsSync = !isSyncLoading && !syncStatus?.lastSyncAt;

  if (needsSync && !isSummaryLoading) {
    return (
      <AppLayout pageTitle="구매주기">
        <NeedsSyncState />
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="구매주기">
      <div className="space-y-6 max-w-6xl mx-auto">
        <AnimatedContainer delay={0}>
          <PageHeader
            title="구매주기"
            description="상품별 평균 재구매 간격과 재구매 타이밍이 도래한 고객을 확인합니다"
            data-testid={testIds.purchaseCycle.pageHeader}
            action={
              summary?.lastCalculatedAt && (
                <span className="text-xs text-muted-foreground">
                  마지막 계산: {new Date(summary.lastCalculatedAt).toLocaleString('ko-KR')}
                </span>
              )
            }
          />
        </AnimatedContainer>

        <AnimatedContainer delay={0.05}>
          <MemberOnlyNotice />
        </AnimatedContainer>

        <AnimatedContainer delay={0.1}>
          <AnalysisGuide title="이 분석은 이렇게 읽어요">
            <p className="text-xs text-muted-foreground/70">
              이 분석의 다른 이름은 <strong>구매주기 분석</strong>이에요.
            </p>
            <p>고객이 보통 며칠 간격으로 다시 사는지 상품별로 알려줘요.</p>
            <p>
              <strong>"구매주기 분포"</strong>는 우리 고객의 재구매 리듬을 한눈에 보여줘요. 쿠폰
              유효기간이나 캠페인 주기를 정할 때 참고하세요.
            </p>
            <p className="mt-1 text-muted-foreground/80">
              <strong>"곧 재구매 예정"</strong>은 아직 안 늦은 고객이에요. 이때 리마인더를 보내면
              전환율이 가장 높아요.
            </p>
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="font-medium text-foreground/80">이렇게 활용해 보세요</p>
              <ul className="mt-1.5 space-y-1 text-muted-foreground list-disc list-inside">
                <li>
                  <strong>재구매 예정 고객</strong>에게 선제 리마인더를 보내면 overdue 되기 전에
                  재구매를 유도할 수 있어요.
                </li>
                <li>
                  <strong>재구매 간격이 짧은 상품</strong>은 정기배송이나 묶음 할인을 제안하면
                  객단가를 높일 수 있어요.
                </li>
                <li>
                  <strong>타이밍 도래 고객</strong>에게는 "지난번 사신 상품, 다시 필요하지
                  않으세요?" 같은 리마인더가 전환율이 높아요.
                </li>
                <li>
                  경과 일수가 <strong>많이 넘긴 고객</strong>일수록 할인 폭을 키우면 재구매 확률이
                  올라가요.
                </li>
              </ul>
            </div>
          </AnalysisGuide>
        </AnimatedContainer>

        <AnimatedContainer delay={0.2}>
          <SummaryCards />
        </AnimatedContainer>
        <AnimatedContainer delay={0.3}>
          <CycleDistributionChart />
        </AnimatedContainer>
        <AnimatedContainer delay={0.4}>
          <UpcomingCustomersTable />
        </AnimatedContainer>
        <AnimatedContainer delay={0.5}>
          <ProductCyclesTable />
        </AnimatedContainer>
        <AnimatedContainer delay={0.6}>
          <OverdueCustomersTable />
        </AnimatedContainer>
      </div>
    </AppLayout>
  );
}
